/**
 * CategorySidebar
 *
 * A sidebar component that displays keyword categories as clickable cards.
 * Each card shows the category name, icon, description, and count of items.
 * Categories are displayed in a fixed order defined by the categoryOrder constant.
 * The first category in the sorted list is selected by default.
 *
 * Used by both the negative-keyword-analyzer agent OutputScreen and the keyword_review behaviour slot.
 */

import React from "react";
import { ListCard } from "@strivelabs/ui-blocks";
import { Badge, cn, ScrollArea } from "@strivelabs/ui-core";
import { categoryOrder, getCategoryDetail } from "./constants";

interface CategorySidebarProps {
  /**
   * List of categories with their counts and selection state
   */
  categories: {
    name: string;
    count: number;
    selected: boolean;
    icon?: React.ReactNode;
  }[];
  /**
   * Callback to be triggered when a category is selected
   */
  onCategorySelect: (category: string) => void;
  /**
   * When true, uses a shorter height for compact contexts (e.g. behaviour slots)
   */
  compact?: boolean;
}

export function CategorySidebar({ categories, onCategorySelect, compact }: CategorySidebarProps) {

  // Sort categories according to the fixed order
  const sortedCategories = React.useMemo(() => {
    return [...categories].sort((a, b) => {
      const indexA = categoryOrder.indexOf(a.name);
      const indexB = categoryOrder.indexOf(b.name);

      // If both categories are in the order list, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one category is in the order list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither category is in the order list, maintain original order
      return 0;
    });
  }, [categories]);

  // Check if any category is selected
  const hasSelectedCategory = sortedCategories.some(category => category.selected);

  // If no category is selected and we have categories, always select the first one from the sorted list
  React.useEffect(() => {
    if (!hasSelectedCategory && sortedCategories.length > 0) {
      // Always select the first category from the sorted list
      onCategorySelect(sortedCategories[0].name);
    }
  }, [hasSelectedCategory, sortedCategories, onCategorySelect]);

  return (
    <div className="w-full h-full">
      <div className="py-4 px-3">
        <h3 className="text-lg font-medium mb-4 px-2">Categories</h3>
        <ScrollArea className={compact ? "h-[300px]" : "md:h-[calc(100vh-250px)] h-[300px]"}>
          <div className="space-y-3 pr-2 pl-1">
            {sortedCategories.map((category) => {
              const { icon, description, displayName } = getCategoryDetail(category.name);
              return (
                <ListCard
                  key={category.name}
                  onClick={() => onCategorySelect(category.name)}
                  className={cn(
                    "p-3 md:p-4",
                    category.selected && "border-primary bg-primary/5"
                  )}
                >
                  <div className="flex items-start w-full">
                    <div className="mr-2 mt-1 flex-shrink-0">
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex justify-between items-center w-full">
                        <span className="font-medium break-words max-w-[70%]">
                          {displayName}
                        </span>
                        <Badge variant="outline" className="ml-2 flex-shrink-0">
                          {category.count}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 overflow-hidden">
                        {description}
                      </p>
                    </div>
                  </div>
                </ListCard>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
